import { useCallback, useEffect, useRef, useState } from 'react';

import {
  proposePieceScoutMove,
  proposeSproutMove,
} from '@/game/ai';
import {
  StockfishGameBridge,
  createStockfishBridgeRequest,
  selectStockfishBridgeLivePosition,
  type StockfishBridgeEffect,
  type StockfishBridgeRequestIdentity,
  type StockfishBridgeSearchLimits,
  type StructuredStockfishEngineClient,
} from '@/game/ai/stockfishBridge';
import {
  StockfishClient,
  createClassicStockfishWorkerFactory,
  type StockfishBestMoveValidator,
} from '@/game/engine';
import {
  createIndexedDbGameSessionPersistence,
  type GameSessionPersistenceFailure,
  type SaveGameSessionResult,
} from '@/game/persistence';
import type { MoveIntent, PromotionPiece } from '@/game/rules';
import {
  createGameSession,
  type AiRequestMetadata,
  type ClockSettings,
  type ComputerAiProfile,
  type GameSession,
  type GameSessionCommand,
  type GameSessionEffect,
  type GameSessionRecord,
  type GameSessionSetup,
  type GameSessionState,
  type GameSessionTransition,
  type HumanColorPreference,
  type PlayerMode,
} from '@/game/session';

export type BeginnerCapyOpponentId = 'sprout' | 'scout';
export type StrongCapyOpponentId = 'curious' | 'grand-master' | 'grand-master-2';
export type CapyOpponentId = BeginnerCapyOpponentId | StrongCapyOpponentId;

export interface StartComputerGameOptions {
  readonly opponent: CapyOpponentId;
  readonly humanColor: HumanColorPreference;
  readonly learnMode: boolean;
  readonly clock?: ClockSettings;
}

export interface StartHotSeatGameOptions {
  readonly clock?: ClockSettings;
}

export type StorageStatus =
  | { readonly phase: 'loading' }
  | { readonly phase: 'ready' }
  | {
      readonly phase: 'conflict';
      readonly error: Extract<GameSessionPersistenceFailure, { readonly kind: 'conflict' }>;
    }
  | {
      readonly phase: 'terminal-save-failed';
      readonly error: Exclude<GameSessionPersistenceFailure, { readonly kind: 'conflict' }>;
    }
  | { readonly phase: 'degraded'; readonly error: GameSessionPersistenceFailure };

export type AppReloadPreparationResult =
  | { readonly ok: true }
  | {
      readonly ok: false;
      readonly reason:
        | 'storage-not-ready'
        | 'persistence-failed'
        | 'persistence-conflict'
        | 'terminal-persistence-failed';
      readonly error?: GameSessionPersistenceFailure;
    };

type TerminalPersistenceFailure = {
  readonly record: GameSessionRecord;
  readonly error: Exclude<GameSessionPersistenceFailure, { readonly kind: 'conflict' }>;
};

export type StrongOpponentUnavailableReason =
  | 'engine-error'
  | 'invalid-result'
  | 'timeout';

export type StrongOpponentStatus =
  | { readonly phase: 'idle' }
  | { readonly phase: 'thinking'; readonly opponent: StrongCapyOpponentId }
  | { readonly phase: 'available'; readonly opponent: StrongCapyOpponentId }
  | {
      readonly phase: 'unavailable';
      readonly opponent: StrongCapyOpponentId;
      readonly reason: StrongOpponentUnavailableReason;
    };

export interface StrongOpponentEngineClient extends StructuredStockfishEngineClient {
  destroy(): void;
}

export type StrongOpponentClientFactory = (
  validateBestMove: StockfishBestMoveValidator,
  opponent: StrongCapyOpponentId,
) => StrongOpponentEngineClient;

export interface UseGameControllerOptions {
  /** Test/runtime seam; callers choose a profile, never raw UCI options. */
  readonly createStrongOpponentClient?: StrongOpponentClientFactory;
  /** Test/runtime seam for a collision-resistant browser game ID. */
  readonly createGameId?: () => string;
  /** Test/runtime seam for the Random side preference. */
  readonly random?: () => number;
}

const STRONG_OPPONENT_CONFIG = Object.freeze({
  curious: Object.freeze({
    workerUrl: `${import.meta.env.BASE_URL}engine/stockfish-18-lite-single.js`,
    hashMb: 16,
    limits: Object.freeze({ nodes: 4_000, depth: 5, multiPv: 12 }),
    handshakeTimeoutMs: 8_000,
    searchTimeoutMs: 12_000,
    stopTimeoutMs: 1_000,
  }),
  'grand-master': Object.freeze({
    workerUrl: `${import.meta.env.BASE_URL}engine/stockfish-18-single.js`,
    hashMb: 64,
    limits: Object.freeze({ moveTimeMs: 15_000, multiPv: 1 }),
    // The first direct-origin load is 113 MB. Give slower client connections
    // time to fetch and compile it before declaring the local engine failed.
    handshakeTimeoutMs: 120_000,
    searchTimeoutMs: 45_000,
    stopTimeoutMs: 2_000,
  }),
  'grand-master-2': Object.freeze({
    workerUrl: `${import.meta.env.BASE_URL}engine/gm2/v1/stockfish-18.0.8-93c99459-full-single-gm2-v1.js`,
    hashMb: 64,
    // The only strength variable versus Grand Master is twice the wall time.
    limits: Object.freeze({ moveTimeMs: 30_000, multiPv: 1 }),
    handshakeTimeoutMs: 120_000,
    searchTimeoutMs: 75_000,
    stopTimeoutMs: 2_000,
  }),
} satisfies Readonly<Record<StrongCapyOpponentId, {
  readonly workerUrl: string;
  readonly hashMb: number;
  readonly limits: StockfishBridgeSearchLimits;
  readonly handshakeTimeoutMs: number;
  readonly searchTimeoutMs: number;
  readonly stopTimeoutMs: number;
}>>);

const OPPONENT_PROFILES: Readonly<Record<CapyOpponentId, ComputerAiProfile>> = {
  sprout: {
    id: 'sprout',
    tierVersion: 'sprout-v1',
    engineBuild: 'capychess-fun-ai-v1',
    gameSeed: 'capychess-sprout-seed-v1',
  },
  scout: {
    id: 'scout',
    tierVersion: 'piece-scout-v1',
    engineBuild: 'capychess-fun-ai-v1',
    gameSeed: 'capychess-scout-seed-v1',
  },
  // Saved games from the retired Stockfish modes safely continue as Sprout.
  curious: {
    id: 'sprout',
    tierVersion: 'sprout-v1',
    engineBuild: 'capychess-fun-ai-v1',
    gameSeed: 'capychess-sprout-seed-v1',
  },
  'grand-master': {
    id: 'sprout',
    tierVersion: 'sprout-v1',
    engineBuild: 'capychess-fun-ai-v1',
    gameSeed: 'capychess-sprout-seed-v1',
  },
  'grand-master-2': {
    id: 'sprout',
    tierVersion: 'sprout-v1',
    engineBuild: 'capychess-fun-ai-v1',
    gameSeed: 'capychess-sprout-seed-v1',
  },
};

function isStrongOpponent(profileId: string): profileId is StrongCapyOpponentId {
  return profileId === 'curious'
    || profileId === 'grand-master'
    || profileId === 'grand-master-2';
}

function createDefaultStrongOpponentClient(
  validateBestMove: StockfishBestMoveValidator,
  opponent: StrongCapyOpponentId,
): StrongOpponentEngineClient {
  const config = STRONG_OPPONENT_CONFIG[opponent];
  // Every pinned browser build advertises a fixed Threads range of 1..1.
  return new StockfishClient({
    createWorker: createClassicStockfishWorkerFactory(config.workerUrl),
    validateBestMove,
    hashMb: config.hashMb,
    handshakeTimeoutMs: config.handshakeTimeoutMs,
    searchTimeoutMs: config.searchTimeoutMs,
    stopTimeoutMs: config.stopTimeoutMs,
  });
}

function normalizeLegacyClubRecord(record: GameSessionRecord): GameSessionRecord {
  if (
    record.mode.kind !== 'computer'
    || !['club', 'curious', 'grand-master', 'grand-master-2'].includes(record.mode.ai.id)
  ) {
    return record;
  }

  const pending = record.ai.pending;
  return Object.freeze({
    ...record,
    mode: Object.freeze({
      ...record.mode,
      ai: Object.freeze({ ...record.mode.ai, id: 'sprout' }),
    }),
    ai: Object.freeze({
      ...record.ai,
      ...(pending === undefined
        ? {}
        : { pending: Object.freeze({ ...pending, profileId: 'sprout' }) }),
    }),
  });
}

function unavailableReasonFor(
  effect: Extract<StockfishBridgeEffect, { readonly type: 'dispatch-ai'; readonly outcome: 'failure' }>,
): StrongOpponentUnavailableReason {
  if (effect.reason === 'engine-error') {
    return effect.message?.toLowerCase().includes('timed out') ? 'timeout' : 'engine-error';
  }
  return 'invalid-result';
}

function requestIdentity(request: AiRequestMetadata): StockfishBridgeRequestIdentity {
  return {
    requestId: request.requestId,
    gameEpoch: request.gameEpoch,
    positionRevision: request.positionRevision,
    fen: request.positionFen,
  };
}

function sameAiRequest(left: AiRequestMetadata, right: AiRequestMetadata): boolean {
  return (
    left.requestId === right.requestId &&
    left.gameEpoch === right.gameEpoch &&
    left.positionRevision === right.positionRevision &&
    left.positionFen === right.positionFen
  );
}

let fallbackGameIdSequence = 0;

function createBrowserGameId(): string {
  const browserCrypto = globalThis.crypto;
  if (typeof browserCrypto?.randomUUID === 'function') {
    return `local-${browserCrypto.randomUUID()}`;
  }
  if (typeof browserCrypto?.getRandomValues === 'function') {
    const values = new Uint32Array(2);
    browserCrypto.getRandomValues(values);
    return `local-${Date.now().toString(36)}-${values[0].toString(36)}${values[1].toString(36)}`;
  }
  fallbackGameIdSequence += 1;
  return `local-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}-${fallbackGameIdSequence.toString(36)}`;
}

function resolveHumanColor(preference: HumanColorPreference, random: () => number): 'w' | 'b' {
  if (preference !== 'random') {
    return preference;
  }
  return random() < 0.5 ? 'w' : 'b';
}

function copyClockSettings(clock: ClockSettings | undefined): ClockSettings | undefined {
  return clock === undefined
    ? undefined
    : { initialMs: clock.initialMs, incrementMs: clock.incrementMs };
}

function hotSeatSetup(createGameId: () => string, options: StartHotSeatGameOptions = {}): GameSessionSetup {
  return {
    gameId: createGameId(),
    mode: { kind: 'hot-seat' },
    ...(options.clock ? { clock: copyClockSettings(options.clock) } : {}),
    startedAtMs: Date.now(),
  };
}

function computerSetup(
  options: StartComputerGameOptions,
  createGameId: () => string,
  random: () => number,
): GameSessionSetup {
  const profile = OPPONENT_PROFILES[options.opponent];
  const gameId = createGameId();
  const humanColor = resolveHumanColor(options.humanColor, random);
  const mode: PlayerMode = {
    kind: 'computer',
    humanColor,
    humanColorPreference: options.humanColor,
    learnMode: options.learnMode,
    ai: {
      ...profile,
      gameSeed: `${profile.gameSeed}:${gameId}`,
    },
  };

  return {
    gameId,
    mode,
    ...(options.clock ? { clock: copyClockSettings(options.clock) } : {}),
    startedAtMs: Date.now(),
  };
}

function rematchSetup(
  state: GameSessionState,
  createGameId: () => string,
  random: () => number,
): GameSessionSetup {
  const gameId = createGameId();
  const clock = state.clock
    ? { initialMs: state.clock.initialMs, incrementMs: state.clock.incrementMs }
    : undefined;
  if (state.mode.kind === 'hot-seat') {
    return {
      gameId,
      initialFen: state.position.initialFen,
      mode: { kind: 'hot-seat' },
      ...(clock ? { clock } : {}),
      startedAtMs: Date.now(),
    };
  }

  const preference = state.mode.humanColorPreference ?? state.mode.humanColor;
  const humanColor = resolveHumanColor(preference, random);
  return {
    gameId,
    initialFen: state.position.initialFen,
    mode: {
      kind: 'computer',
      humanColor,
      humanColorPreference: preference,
      learnMode: state.mode.learnMode,
      ai: {
        ...state.mode.ai,
        gameSeed: `${state.mode.ai.gameSeed}:${gameId}`,
      },
    },
    ...(clock ? { clock } : {}),
    startedAtMs: Date.now(),
  };
}

function pendingMatches(state: GameSessionState, request: AiRequestMetadata): boolean {
  const pending = state.ai.pending;
  return (
    pending !== undefined &&
    pending.requestId === request.requestId &&
    pending.gameEpoch === request.gameEpoch &&
    pending.positionRevision === request.positionRevision &&
    pending.positionFen === request.positionFen
  );
}

/**
 * Restoration creates a new local game epoch and may issue a local AI request.
 * Neither belongs to the shared session branch, so they must not advance the
 * cross-tab slot generation by themselves.
 */
function restoreNeedsPersistence(
  stored: GameSessionRecord,
  restored: GameSessionRecord,
  location: 'current' | 'completed',
): boolean {
  // A terminal value in the current slot is an interrupted archive and must
  // be replayed into the completed store even when its game data is unchanged.
  if (location === 'current' && stored.result.terminal) {
    return true;
  }

  return JSON.stringify(restoreDurableProjection(stored))
    !== JSON.stringify(restoreDurableProjection(restored));
}

function restoreDurableProjection(record: GameSessionRecord) {
  return {
    schemaVersion: record.schemaVersion,
    gameId: record.gameId,
    createdAtMs: record.createdAtMs,
    initialFen: record.initialFen,
    acceptedUciMoves: record.acceptedUciMoves,
    acceptedMoves: record.acceptedMoves,
    derivedFen: record.derivedFen,
    result: record.result,
    mode: record.mode,
    ...(record.clock === undefined ? {} : { clock: record.clock }),
    // Older valid records predate this field; an omitted chain and an empty
    // chain are equivalent restore state.
    clockTimelineAnchors: record.clockTimelineAnchors ?? [],
  };
}

/**
 * React adapter for the reducer-like GameSession domain. The domain remains
 * the only mutation owner; this hook only schedules cancellable local bot work.
 */
export function useGameController(options: UseGameControllerOptions = {}) {
  const createGameIdRef = useRef(options.createGameId ?? createBrowserGameId);
  const randomRef = useRef(options.random ?? Math.random);
  const gameRef = useRef<GameSession | null>(null);
  if (gameRef.current === null) {
    gameRef.current = createGameSession(hotSeatSetup(createGameIdRef.current));
  }

  const [state, setState] = useState<GameSessionState>(() => gameRef.current!.snapshot());
  const [storageStatus, setStorageStatus] = useState<StorageStatus>({ phase: 'loading' });
  const [strongOpponentStatus, setStrongOpponentStatus] = useState<StrongOpponentStatus>({
    phase: 'idle',
  });
  const [clockNowMs, setClockNowMs] = useState(() => Date.now());
  const timerByRequest = useRef(new Map<number, number>());
  const effectsRef = useRef<(effects: readonly GameSessionEffect[]) => void>(() => undefined);
  const persistenceRef = useRef(createIndexedDbGameSessionPersistence());
  const createStrongOpponentClientRef = useRef(
    options.createStrongOpponentClient ?? createDefaultStrongOpponentClient,
  );
  const strongOpponentClientRef = useRef<StrongOpponentEngineClient | null>(null);
  const strongOpponentClientProfileRef = useRef<StrongCapyOpponentId | null>(null);
  const stockfishBridgeRef = useRef<StockfishGameBridge | null>(null);
  const strongOpponentRequestRef = useRef<AiRequestMetadata | null>(null);
  const writeTail = useRef(Promise.resolve());
  const mountedRef = useRef(true);
  const hydratedRef = useRef(false);
  const persistenceConflictRef = useRef<Extract<
    GameSessionPersistenceFailure,
    { readonly kind: 'conflict' }
  > | null>(null);
  const terminalPersistenceFailureRef = useRef<TerminalPersistenceFailure | null>(null);
  const terminalPersistencePendingRef = useRef<GameSessionRecord | null>(null);
  const reloadHandoffRef = useRef(false);
  const reloadPreparationRef = useRef<Promise<AppReloadPreparationResult> | null>(null);

  const validateStrongOpponentMove = useCallback<StockfishBestMoveValidator>((input) => {
    const current = gameRef.current?.snapshot();
    const pending = current?.ai.pending;
    return (
      current !== undefined &&
      pending !== undefined &&
      isStrongOpponent(pending.profileId) &&
      input.bestMove !== null &&
      current.gameEpoch === input.request.gameEpoch &&
      current.position.revision === input.request.positionRevision &&
      current.position.fen === input.request.fen &&
      pending.gameEpoch === input.request.gameEpoch &&
      pending.positionRevision === input.request.positionRevision &&
      pending.positionFen === input.request.fen &&
      current.position.legalUciMoves.includes(input.bestMove)
    );
  }, []);

  const getStockfishBridge = useCallback((opponent: StrongCapyOpponentId): StockfishGameBridge => {
    const existing = stockfishBridgeRef.current;
    if (existing !== null && strongOpponentClientProfileRef.current === opponent) {
      return existing;
    }

    // A completed strong-opponent game may leave its lazy Worker warm. Every
    // profile owns its exact generation, so profile changes always replace it.
    strongOpponentClientRef.current?.destroy();
    strongOpponentClientRef.current = null;
    strongOpponentClientProfileRef.current = null;
    stockfishBridgeRef.current = null;

    let client: StrongOpponentEngineClient;
    try {
      client = createStrongOpponentClientRef.current(validateStrongOpponentMove, opponent);
    } catch (error) {
      client = {
        search: () => Promise.reject(error),
        cancel: () => undefined,
        destroy: () => undefined,
      };
    }

    const bridge = new StockfishGameBridge({
      engine: client,
      readLivePosition: () => {
        const game = gameRef.current;
        if (game === null) {
          throw new Error('CapyChess game controller was disposed unexpectedly.');
        }
        return selectStockfishBridgeLivePosition(game.snapshot());
      },
      now: Date.now,
    });
    strongOpponentClientRef.current = client;
    strongOpponentClientProfileRef.current = opponent;
    stockfishBridgeRef.current = bridge;
    return bridge;
  }, [validateStrongOpponentMove]);

  const disposeStrongOpponent = useCallback((request?: AiRequestMetadata) => {
    const active = strongOpponentRequestRef.current;
    if (request !== undefined && active !== null && !sameAiRequest(active, request)) {
      return;
    }

    const requestToCancel = request ?? active;
    if (requestToCancel !== null && requestToCancel !== undefined) {
      stockfishBridgeRef.current?.cancel(requestIdentity(requestToCancel));
    }
    strongOpponentClientRef.current?.destroy();
    stockfishBridgeRef.current = null;
    strongOpponentClientRef.current = null;
    strongOpponentClientProfileRef.current = null;
    strongOpponentRequestRef.current = null;
  }, []);

  const lockForPersistenceConflict = useCallback((error: Extract<
    GameSessionPersistenceFailure,
    { readonly kind: 'conflict' }
  >) => {
    if (persistenceConflictRef.current !== null) {
      return;
    }
    terminalPersistencePendingRef.current = null;
    terminalPersistenceFailureRef.current = null;
    persistenceConflictRef.current = error;
    for (const timer of timerByRequest.current.values()) {
      window.clearTimeout(timer);
    }
    timerByRequest.current.clear();
    disposeStrongOpponent();
    if (mountedRef.current) {
      setStrongOpponentStatus({ phase: 'idle' });
      setStorageStatus({ phase: 'conflict', error });
    }
  }, [disposeStrongOpponent]);

  const lockForTerminalPersistenceFailure = useCallback((
    record: GameSessionRecord,
    error: Exclude<GameSessionPersistenceFailure, { readonly kind: 'conflict' }>,
  ) => {
    terminalPersistencePendingRef.current = null;
    terminalPersistenceFailureRef.current = { record, error };
    for (const timer of timerByRequest.current.values()) {
      window.clearTimeout(timer);
    }
    timerByRequest.current.clear();
    disposeStrongOpponent();
    if (mountedRef.current) {
      setStrongOpponentStatus({ phase: 'idle' });
      setStorageStatus({ phase: 'terminal-save-failed', error });
    }
  }, [disposeStrongOpponent]);

  const enqueuePersistence = useCallback((
    record: GameSessionRecord,
    retryTerminal = false,
  ): Promise<SaveGameSessionResult> => {
    if (
      record.result.terminal
      && (
        terminalPersistenceFailureRef.current === null
        || (retryTerminal && terminalPersistenceFailureRef.current.record === record)
      )
    ) {
      // Lock replacement commands before an asynchronous archive result is
      // known; otherwise a click between failure and its callback can queue a
      // new active record behind the terminal archive.
      terminalPersistencePendingRef.current = record;
    }
    const persistence = persistenceRef.current;
    const operation = writeTail.current
      .catch(() => undefined)
      .then<SaveGameSessionResult>(async () => {
        const existingConflict = persistenceConflictRef.current;
        if (existingConflict !== null) {
          return { ok: false, error: existingConflict };
        }
        const terminalFailure = terminalPersistenceFailureRef.current;
        if (retryTerminal) {
          if (terminalFailure === null) {
            return { ok: true };
          }
          if (terminalFailure.record !== record) {
            return { ok: false, error: terminalFailure.error };
          }
        } else if (terminalFailure !== null) {
          return { ok: false, error: terminalFailure.error };
        }
        let result: SaveGameSessionResult;
        try {
          result = await persistence.save(record);
        } catch (cause) {
          result = { ok: false, error: { kind: 'write-failed', cause } };
        }
        if (!result.ok) {
          if (result.error.kind === 'conflict') {
            lockForPersistenceConflict(result.error);
            return result;
          }
          if (record.result.terminal) {
            lockForTerminalPersistenceFailure(record, result.error);
            return result;
          }
        }
        if (result.ok && retryTerminal) {
          terminalPersistenceFailureRef.current = null;
        }
        if (result.ok && record.result.terminal && terminalPersistencePendingRef.current === record) {
          terminalPersistencePendingRef.current = null;
        }
        if (!mountedRef.current) {
          return result;
        }
        if (result.ok) {
          setStorageStatus({ phase: 'ready' });
        } else {
          setStorageStatus({ phase: 'degraded', error: result.error });
        }
        return result;
      });
    writeTail.current = operation.then(() => undefined);
    return operation;
  }, [lockForPersistenceConflict, lockForTerminalPersistenceFailure]);

  const runEffects = useCallback((effects: readonly GameSessionEffect[]) => {
    if (persistenceConflictRef.current !== null || terminalPersistenceFailureRef.current !== null) {
      return;
    }
    for (const effect of effects) {
      if (effect.type === 'persist') {
        void enqueuePersistence(effect.record);
        continue;
      }

      if (effect.type === 'cancel-ai') {
        const timer = timerByRequest.current.get(effect.request.requestId);
        if (timer !== undefined) {
          window.clearTimeout(timer);
          timerByRequest.current.delete(effect.request.requestId);
        }
        if (isStrongOpponent(effect.request.profileId)) {
          disposeStrongOpponent(effect.request);
          if (mountedRef.current) {
            setStrongOpponentStatus({ phase: 'idle' });
          }
        }
        continue;
      }

      if (isStrongOpponent(effect.request.profileId)) {
        const opponent: StrongCapyOpponentId = effect.request.profileId;
        const request = effect.request;
        strongOpponentRequestRef.current = request;
        setStrongOpponentStatus({ phase: 'thinking', opponent });
        const bridge = getStockfishBridge(opponent);
        const bridgeRequest = createStockfishBridgeRequest(
          request,
          STRONG_OPPONENT_CONFIG[opponent].limits,
        );

        void bridge.request(bridgeRequest).then((bridgeEffect) => {
          if (
            !mountedRef.current
            || persistenceConflictRef.current !== null
            || terminalPersistenceFailureRef.current !== null
          ) {
            return;
          }

          const active = strongOpponentRequestRef.current;
          if (bridgeEffect.type === 'discard-ai') {
            if (active !== null && sameAiRequest(active, request)) {
              strongOpponentRequestRef.current = null;
              setStrongOpponentStatus({ phase: 'idle' });
            }
            return;
          }

          const game = gameRef.current;
          if (game === null) {
            return;
          }
          const before = game.snapshot();
          const transition = game.dispatch(bridgeEffect.command);
          if (transition.state === before) {
            return;
          }

          strongOpponentRequestRef.current = null;
          setState(transition.state);
          if (bridgeEffect.outcome === 'failure') {
            disposeStrongOpponent();
            setStrongOpponentStatus({
              phase: 'unavailable',
              opponent,
              reason: unavailableReasonFor(bridgeEffect),
            });
          } else {
            setStrongOpponentStatus({ phase: 'available', opponent });
          }
          effectsRef.current(transition.effects);
        });
        continue;
      }

      const delayMs = effect.request.profileId === 'scout' ? 720 : 430;
      const timer = window.setTimeout(() => {
        timerByRequest.current.delete(effect.request.requestId);
        const game = gameRef.current;
        if (
          game === null
          || persistenceConflictRef.current !== null
          || terminalPersistenceFailureRef.current !== null
          || !pendingMatches(game.snapshot(), effect.request)
        ) {
          return;
        }

        const request = effect.request;
        const proposalRequest = {
          fen: request.positionFen,
          gameSeed: request.gameSeed,
          tierVersion: request.tierVersion,
          engineBuild: request.engineBuild,
          ply: request.ply,
        };
        const proposal = request.profileId === 'scout'
          ? proposePieceScoutMove(proposalRequest)
          : proposeSproutMove(proposalRequest);
        const transition = game.dispatch({
          type: 'resolve-ai',
          requestId: request.requestId,
          gameEpoch: request.gameEpoch,
          positionRevision: request.positionRevision,
          positionFen: request.positionFen,
          uci: proposal.status === 'ok' ? proposal.uci : null,
          atMs: Date.now(),
        });
        setState(transition.state);
        effectsRef.current(transition.effects);
      }, delayMs);
      timerByRequest.current.set(effect.request.requestId, timer);
    }
  }, [disposeStrongOpponent, enqueuePersistence, getStockfishBridge]);

  effectsRef.current = runEffects;

  const resumeAfterAppReloadFailure = useCallback(() => {
    if (!reloadHandoffRef.current) {
      return;
    }
    reloadHandoffRef.current = false;
    reloadPreparationRef.current = null;
    if (
      persistenceConflictRef.current !== null
      || terminalPersistenceFailureRef.current !== null
    ) {
      return;
    }
    const pending = gameRef.current?.snapshot().ai.pending;
    if (pending !== undefined && mountedRef.current) {
      effectsRef.current([{ type: 'request-ai', request: pending }]);
    }
  }, []);

  const prepareForAppReload = useCallback((): Promise<AppReloadPreparationResult> => {
    const existing = reloadPreparationRef.current;
    if (existing !== null) {
      return existing;
    }

    const conflict = persistenceConflictRef.current;
    if (conflict !== null) {
      return Promise.resolve({ ok: false, reason: 'persistence-conflict', error: conflict });
    }
    const terminalFailure = terminalPersistenceFailureRef.current;
    if (terminalFailure !== null) {
      return Promise.resolve({
        ok: false,
        reason: 'terminal-persistence-failed',
        error: terminalFailure.error,
      });
    }

    const game = gameRef.current;
    if (!hydratedRef.current || game === null) {
      return Promise.resolve({ ok: false, reason: 'storage-not-ready' });
    }

    reloadHandoffRef.current = true;
    for (const timer of timerByRequest.current.values()) {
      window.clearTimeout(timer);
    }
    timerByRequest.current.clear();
    disposeStrongOpponent();

    const preparation = enqueuePersistence(game.toRecord()).then((result): AppReloadPreparationResult => {
      if (result.ok) {
        return { ok: true };
      }

      resumeAfterAppReloadFailure();
      return { ok: false, reason: 'persistence-failed', error: result.error };
    });
    reloadPreparationRef.current = preparation;
    return preparation;
  }, [disposeStrongOpponent, enqueuePersistence, resumeAfterAppReloadFailure]);

  const retryTerminalPersistence = useCallback((): Promise<SaveGameSessionResult> => {
    const terminalFailure = terminalPersistenceFailureRef.current;
    if (terminalFailure === null) {
      return Promise.resolve({ ok: true });
    }
    return enqueuePersistence(terminalFailure.record, true);
  }, [enqueuePersistence]);

  useEffect(() => {
    let active = true;
    mountedRef.current = true;
    hydratedRef.current = false;
    setStorageStatus({ phase: 'loading' });
    const game = gameRef.current;
    if (game === null) {
      return () => undefined;
    }
    const expectedGameEpoch = game.snapshot().gameEpoch;

    void persistenceRef.current.read().then((read) => {
      if (!active) {
        return;
      }

      const stored = read.ok ? read.value : read.fallback;
      const candidates = [
        ...(stored.recoverableTerminal
          ? [{ record: stored.recoverableTerminal, location: 'current' as const }]
          : []),
        ...(stored.current ? [{ record: stored.current, location: 'current' as const }] : []),
        ...stored.completed.map((record) => ({ record, location: 'completed' as const })),
      ];
      const initialEffects = game.takeInitialEffects();
      hydratedRef.current = true;

      let restored = false;
      let rejectedLocation: 'current' | 'completed' | undefined;
      for (const candidate of candidates) {
        const beforeRestore = game.snapshot();
        const transition = game.dispatch({
          type: 'restore-record',
          expectedGameEpoch,
          record: normalizeLegacyClubRecord(candidate.record),
          atMs: Date.now(),
        });
        if (transition.state === beforeRestore) {
          rejectedLocation ??= candidate.location;
          continue;
        }

        restored = true;
        setState(transition.state);
        const restoredRecord = game.toRecord();
        runEffects(
          restoreNeedsPersistence(candidate.record, restoredRecord, candidate.location)
            ? transition.effects
            : transition.effects.filter((effect) => effect.type !== 'persist'),
        );
        break;
      }

      // A clean, genuinely empty database may be initialized automatically.
      // Any failed read or rejected stored record must remain untouched until a
      // validated fallback/archive is restored or the player explicitly starts
      // a new game.
      if (!restored && candidates.length === 0 && read.ok) {
        runEffects(initialEffects);
      }

      if (!read.ok) {
        setStorageStatus({ phase: 'degraded', error: read.error });
      } else if (rejectedLocation !== undefined) {
        setStorageStatus({
          phase: 'degraded',
          error: { kind: 'invalid-record', location: rejectedLocation },
        });
      } else {
        setStorageStatus({ phase: 'ready' });
      }
    });

    return () => {
      active = false;
      mountedRef.current = false;
      for (const timer of timerByRequest.current.values()) {
        window.clearTimeout(timer);
      }
      timerByRequest.current.clear();
      disposeStrongOpponent();
      persistenceRef.current.close();
    };
  }, [disposeStrongOpponent, runEffects]);

  const dispatch = useCallback((command: GameSessionCommand): GameSessionTransition => {
    const game = gameRef.current;
    if (game === null) {
      throw new Error('CapyChess game controller was disposed unexpectedly.');
    }
    if (
      reloadHandoffRef.current
      || persistenceConflictRef.current !== null
      || terminalPersistenceFailureRef.current !== null
      || terminalPersistencePendingRef.current !== null
    ) {
      return { state: game.snapshot(), effects: Object.freeze([]) };
    }

    const transition = game.dispatch(command);
    setState(transition.state);
    runEffects(transition.effects);
    return transition;
  }, [runEffects]);

  useEffect(() => {
    const clock = state.clock;
    if (clock === undefined || state.result.terminal || state.review !== undefined) {
      return undefined;
    }

    const tick = () => {
      const game = gameRef.current;
      if (
        game === null
        || reloadHandoffRef.current
        || persistenceConflictRef.current !== null
        || terminalPersistenceFailureRef.current !== null
        || terminalPersistencePendingRef.current !== null
      ) {
        return;
      }
      const before = game.snapshot();
      if (before.clock === undefined || before.result.terminal || before.review !== undefined) {
        return;
      }
      const atMs = Date.now();
      setClockNowMs(atMs);
      const transition = game.dispatch({ type: 'reconcile-clock', atMs });
      if (transition.state !== before) {
        setState(transition.state);
        runEffects(transition.effects);
      }
    };

    tick();
    const timer = window.setInterval(tick, 250);
    return () => window.clearInterval(timer);
  }, [runEffects, state.clock?.activeDeadlineMs, state.result.terminal, state.review]);

  const submitMoveIntent = useCallback((intent: MoveIntent): GameSessionState => {
    const current = gameRef.current?.snapshot();
    if (!current) {
      return state;
    }
    // The callback itself is a board-timeline capability. Keep the identity
    // from the render that handed it to BoardPort; consulting only the latest
    // ref would let an old callback pass an ABA-equal rules revision after a
    // restore replaced the whole game branch.
    if (current.gameId !== state.gameId || current.gameEpoch !== state.gameEpoch) {
      return current;
    }
    return dispatch({
      type: 'submit-move',
      gameEpoch: state.gameEpoch,
      positionRevision: intent.expectedRevision,
      from: intent.from,
      to: intent.to,
      atMs: Date.now(),
    }).state;
  }, [dispatch, state]);

  const submitUci = useCallback((uci: string): GameSessionState => {
    const normalized = uci.trim().toLowerCase();
    const current = gameRef.current?.snapshot();
    if (!current || !/^[a-h][1-8][a-h][1-8][qrbn]?$/.test(normalized)) {
      return current ?? state;
    }

    return dispatch({
      type: 'submit-move',
      gameEpoch: current.gameEpoch,
      positionRevision: current.position.revision,
      from: normalized.slice(0, 2),
      to: normalized.slice(2, 4),
      ...(normalized.length === 5 ? { promotion: normalized[4] as PromotionPiece } : {}),
      atMs: Date.now(),
    }).state;
  }, [dispatch, state]);

  const choosePromotion = useCallback((promotion: PromotionPiece): GameSessionState => {
    const current = gameRef.current?.snapshot();
    if (!current) {
      return state;
    }
    return dispatch({
      type: 'choose-promotion',
      gameEpoch: current.gameEpoch,
      positionRevision: current.position.revision,
      promotion,
      atMs: Date.now(),
    }).state;
  }, [dispatch, state]);

  const cancelPromotion = useCallback((): GameSessionState => {
    const current = gameRef.current?.snapshot();
    if (!current) {
      return state;
    }
    return dispatch({
      type: 'cancel-promotion',
      gameEpoch: current.gameEpoch,
      positionRevision: current.position.revision,
    }).state;
  }, [dispatch, state]);

  const startHotSeat = useCallback((options: StartHotSeatGameOptions = {}): GameSessionState => {
    const current = gameRef.current!.snapshot();
    if (!hydratedRef.current) {
      return current;
    }
    disposeStrongOpponent();
    setStrongOpponentStatus({ phase: 'idle' });
    return dispatch({
      type: 'new-game',
      expectedGameEpoch: current.gameEpoch,
      setup: hotSeatSetup(createGameIdRef.current, options),
      atMs: Date.now(),
    }).state;
  }, [dispatch, disposeStrongOpponent]);

  const startComputerGame = useCallback((options: StartComputerGameOptions): GameSessionState => {
    const current = gameRef.current!.snapshot();
    if (!hydratedRef.current) {
      return current;
    }
    if (!isStrongOpponent(options.opponent)) {
      disposeStrongOpponent();
    }
    setStrongOpponentStatus({ phase: 'idle' });
    return dispatch({
      type: 'new-game',
      expectedGameEpoch: current.gameEpoch,
      setup: computerSetup(options, createGameIdRef.current, randomRef.current),
      atMs: Date.now(),
    }).state;
  }, [dispatch, disposeStrongOpponent]);

  const resign = useCallback((): GameSessionState => {
    const current = gameRef.current!.snapshot();
    return dispatch({ type: 'resign', gameEpoch: current.gameEpoch, atMs: Date.now() }).state;
  }, [dispatch]);

  const agreeDraw = useCallback((): GameSessionState => {
    const current = gameRef.current!.snapshot();
    if (!hydratedRef.current) {
      return current;
    }
    return dispatch({ type: 'agree-draw', gameEpoch: current.gameEpoch, atMs: Date.now() }).state;
  }, [dispatch]);

  const rematch = useCallback((): GameSessionState => {
    const current = gameRef.current!.snapshot();
    if (!hydratedRef.current || !current.result.terminal) {
      return current;
    }
    if (current.mode.kind !== 'computer' || !isStrongOpponent(current.mode.ai.id)) {
      disposeStrongOpponent();
    }
    setStrongOpponentStatus({ phase: 'idle' });
    return dispatch({
      type: 'new-game',
      expectedGameEpoch: current.gameEpoch,
      setup: rematchSetup(current, createGameIdRef.current, randomRef.current),
      atMs: Date.now(),
    }).state;
  }, [dispatch, disposeStrongOpponent]);

  const reconcileClock = useCallback((): GameSessionState => {
    const current = gameRef.current!.snapshot();
    return dispatch({ type: 'reconcile-clock', atMs: Date.now() }).state;
  }, [dispatch]);

  const undoLearn = useCallback((): GameSessionState => {
    const current = gameRef.current!.snapshot();
    const next = dispatch({
      type: 'undo-learn',
      gameEpoch: current.gameEpoch,
      atMs: Date.now(),
    }).state;
    if (current.mode.kind === 'computer' && isStrongOpponent(current.mode.ai.id)) {
      disposeStrongOpponent();
      setStrongOpponentStatus({ phase: 'idle' });
    }
    return next;
  }, [dispatch, disposeStrongOpponent]);

  const selectReviewPly = useCallback((ply: number): GameSessionState => {
    const current = gameRef.current!.snapshot();
    const next = dispatch({
      type: 'select-review-ply', gameEpoch: current.gameEpoch, ply }).state;
    if (
      next !== current &&
      next.review !== undefined &&
      current.mode.kind === 'computer' &&
      isStrongOpponent(current.mode.ai.id)
    ) {
      disposeStrongOpponent();
      setStrongOpponentStatus({ phase: 'idle' });
    }
    return next;
  }, [dispatch, disposeStrongOpponent]);

  const exitReview = useCallback((): GameSessionState => {
    const current = gameRef.current!.snapshot();
    return dispatch({ type: 'exit-review', gameEpoch: current.gameEpoch, atMs: Date.now() }).state;
  }, [dispatch]);

  return {
    state,
    storageStatus,
    strongOpponentStatus,
    isHydrated: storageStatus.phase !== 'loading',
    clockNowMs,
    submitMoveIntent,
    submitUci,
    choosePromotion,
    cancelPromotion,
    startHotSeat,
    startComputerGame,
    resign,
    agreeDraw,
    rematch,
    reconcileClock,
    undoLearn,
    selectReviewPly,
    exitReview,
    prepareForAppReload,
    resumeAfterAppReloadFailure,
    retryTerminalPersistence,
  };
}
